import React from 'react';
import GlobalStyles from '../../base/ui/components/GlobalStyles.web';
import JitsiThemeProvider from '../../base/ui/components/JitsiThemeProvider.web';
import DialogContainer from '../../base/ui/components/web/DialogContainer';
import { FEATURES } from '../../base/whitelabel/constants';
import { isFeatureEnabled } from '../../base/whitelabel/functions';
import ChromeExtensionBanner from '../../chrome-extension-banner/components/ChromeExtensionBanner.web';
import HandoffReceiver from '../../device-handoff/components/HandoffReceiver';
import OverlayContainer from '../../overlay/components/web/OverlayContainer';
import VoicemailFab from '../../voicemail/components/web/VoicemailFab';
import VoicemailInbox from '../../voicemail/components/web/VoicemailInbox';
import VoicemailPrompt from '../../voicemail/components/web/VoicemailPrompt';
import VoicemailRecording from '../../voicemail/components/web/VoicemailRecording';
import VoicemailPlayer from '../../voicemail/components/web/VoicemailPlayer';
import { beginJoinMarks, markJoin } from '../../conference/joinMarks.web';
import { installMobileErrorReporting } from '../../mobile/navigation/errorReporting';
import { whenRoomActivated } from '../roomActivation.web';
import { reportRoomBoot } from '../roomBootTelemetry.web';
import { AbstractApp } from './AbstractApp';
// Register middlewares and reducers.
import '../middlewares';
import '../reducers';
// The web room page had zero failure coverage: reporting only installed on
// native. The reporter is web-safe via the react-native shim; the room boot
// breadcrumbs (conference pages only) bracket the gap, see reportRoomBoot.
// A room page a portal prerendered does all of this once it is shown, after
// the handoff has put the real room URL in place; every other page does it
// now, as the bundle loads (roomActivation.web.ts).
whenRoomActivated(() => {
    installMobileErrorReporting();
    const roomBoot = reportRoomBoot();
    // A room page reports its own critical path once (vrs_join_marks).
    if (roomBoot) {
        beginJoinMarks(roomBoot);
    }
});
/**
 * Root app {@code Component} on Web/React.
 *
 * @augments AbstractApp
 */
export class App extends AbstractApp {
    /**
     * Marks the app's mount for the room's join marks, then initializes.
     *
     * @inheritdoc
     */
    async componentDidMount() {
        markJoin('appMount');
        await super.componentDidMount();
    }
    /**
     * Creates an extra {@link ReactElement}s to be added (unconditionally)
     * alongside the main element.
     *
     * @abstract
     * @protected
     * @returns {ReactElement}
     */
    _createExtraElement() {
        const isVoicemailAvailable = isFeatureEnabled(FEATURES.VOICEMAIL);
        return (React.createElement(JitsiThemeProvider, null,
            React.createElement(OverlayContainer, null),
            React.createElement(HandoffReceiver, null),
            isVoicemailAvailable && (React.createElement(React.Fragment, null,
                React.createElement(VoicemailFab, null),
                React.createElement(VoicemailInbox, null),
                React.createElement(VoicemailPrompt, null),
                React.createElement(VoicemailRecording, null),
                React.createElement(VoicemailPlayer, null)))));
    }
    /**
     * Overrides the parent method to inject {@link AtlasKitThemeProvider} as
     * the top most component.
     *
     * @override
     */
    _createMainElement(component, props) {
        return (React.createElement(JitsiThemeProvider, null,
            React.createElement(GlobalStyles, null),
            React.createElement(ChromeExtensionBanner, null),
            super._createMainElement(component, props)));
    }
    /**
     * Renders the platform specific dialog container.
     *
     * @returns {React$Element}
     */
    _renderDialogContainer() {
        return (React.createElement(JitsiThemeProvider, null,
            React.createElement(DialogContainer, null)));
    }
}
